import { z } from "zod";

import { RoleResourceTypeSchema } from "../../../../shared/domain/roles/role.types";
import {
  RolePlacementTableSchema,
  RoleReleaseEffectSchema,
  RoleRuntimeReachEffectSchema
} from "../../../../shared/domain/roles/role-deletion-plan.types";

/**
 * Deletion-plan transport — what a Role holds, what releasing each element
 * costs, and the decisions that re-home them before the Role goes.
 *
 * The closed sets these schemas are built from live in
 * `shared/domain/roles/role-deletion-plan.types.ts`, which is VOCABULARY the
 * backend switches on. This file is the ENVELOPE, and the split is load-bearing
 * rather than tidy: the backend reaches `@nexus/types` and cannot reach
 * `api/domains/roles` at all.
 */

/**
 * One re-homable element the Role holds.
 *
 * `name` is REQUIRED and never blank. An element the server cannot name is not
 * listed at all — the same rule `HeldResourceNamesService` already applies to
 * the system map, and for the same reason: an id ADDRESSES a thing and does not
 * LABEL one, so a row reading "Untitled" asks a person to make an irreversible
 * decision about something they cannot identify.
 */
export const RoleDeletionInventoryItemSchema = z.strictObject({
  /** Which of the four placement tables this row lives in. */
  placement: RolePlacementTableSchema,
  /**
   * That row's own primary key — `RoleResource.id` or the grant row's `id`. This
   * is what a decision addresses, and it is a row id rather than a natural key
   * so that a concurrent move cannot re-point what a decision meant.
   */
  placementId: z.string().uuid(),
  /**
   * Non-null EXACTLY when `placement` is `"resource"`. The three grant tables
   * each hold one kind, so the table already says what the element is.
   */
  resourceType: RoleResourceTypeSchema.nullable(),
  /** The thing being held: a Collection, Workspace, tool, agent, workflow, ... */
  subjectId: z.string(),
  /** The human label. Never blank — see the schema doc. */
  name: z.string().min(1),
  /**
   * Does at least one OTHER Role in this organisation hold this same element?
   *
   * 🚨 STRUCTURALLY ALWAYS `false` FOR `placement: "resource"`, and that is a
   * fact about the schema rather than about the data:
   * `RoleResource @@unique([organizationId, resourceType, resourceId])` makes a
   * resource belong to at most ONE Role org-wide. Only the grant tables permit
   * sharing.
   *
   * Read this as an INPUT to `releaseEffect`, never as the thing to render —
   * `releaseEffect` is the computed answer and already folds it in. Rendering a
   * warning from this boolean directly puts one on every `resource` row and none
   * on a shared Collection, which is backwards on both.
   */
  heldByAnotherRole: z.boolean(),
  /** What releasing it does to who can reach it. */
  releaseEffect: RoleReleaseEffectSchema,
  /** What releasing it does to what IT can reach. Opposite direction. */
  runtimeReachEffect: RoleRuntimeReachEffectSchema
});

export type RoleDeletionInventoryItem = z.infer<typeof RoleDeletionInventoryItemSchema>;

/** A Role an element can be shipped to. Id and name only — a picker needs nothing else. */
export const RoleReassignmentTargetSchema = z.strictObject({
  id: z.string().uuid(),
  name: z.string()
});

export type RoleReassignmentTarget = z.infer<typeof RoleReassignmentTargetSchema>;

/**
 * Everything the Role holds, plus the Roles a caller may ship an element to.
 *
 * AN EMPTY `items` ARRAY IS THE SKIP SIGNAL. A Role holding nothing re-homable
 * needs no wizard, and the existing type-to-confirm delete panel is already
 * correct for it. There is deliberately no second deletion path.
 */
export const RoleDeletionInventorySchema = z.strictObject({
  items: z.array(RoleDeletionInventoryItemSchema),
  reassignmentTargets: z.array(RoleReassignmentTargetSchema)
});

export type RoleDeletionInventory = z.infer<typeof RoleDeletionInventorySchema>;

/**
 * One decision.
 *
 * A discriminated union rather than an optional `targetRoleId`, so "REASSIGN
 * with no target" and "RELEASE with a target" are both inexpressible rather than
 * validated against. The wire cannot carry a decision the server would have to
 * refuse.
 */
export const RoleDeletionDecisionSchema = z.discriminatedUnion("action", [
  z.strictObject({
    action: z.literal("REASSIGN"),
    placement: RolePlacementTableSchema,
    placementId: z.string().uuid(),
    targetRoleId: z.string().uuid()
  }),
  z.strictObject({
    action: z.literal("RELEASE"),
    placement: RolePlacementTableSchema,
    placementId: z.string().uuid()
  })
]);

export type RoleDeletionDecision = z.infer<typeof RoleDeletionDecisionSchema>;

/**
 * The wizard's commit step: apply every decision, then delete the Role.
 *
 * 🔴 `decisions` MUST NAME EVERY ELEMENT THE INVENTORY RETURNED, and the server
 * refuses a body that does not. An omitted element is indistinguishable from an
 * element the caller never saw, and defaulting it to RELEASE would publish a
 * resource nobody was asked about — the exact harm this feature exists to
 * prevent. Refusing is the only safe reading of an incomplete plan.
 *
 * A RELEASE decision performs NO WRITE OF ITS OWN. Deleting the Role already
 * empties all four placement tables by cascade; `DeleteRolePrismaAdapter`
 * already reconciles `accessScope` for the grants, under a `FOR UPDATE` lock
 * that serialises two concurrent Role deletes, and `DeleteRoleUseCase` already
 * reopens the `ResourcePermission` rows. A release is therefore the caller
 * ACCEPTING that reconciliation rather than requesting a second one — a separate
 * release write would be a rival implementation of a cascade that is already
 * correct, and a rival implementation of this exact surface is how it has been
 * broken before.
 */
export const RoleDeletionPlanBodySchema = z.strictObject({
  decisions: z.array(RoleDeletionDecisionSchema)
});

export type RoleDeletionPlanBody = z.infer<typeof RoleDeletionPlanBodySchema>;

/**
 * What the commit did.
 *
 * The `pending` arm is NOT a wizard concept — it is `DeleteRoleOrRequestUseCase`
 * showing through, and it must. Deleting a Role can require approval, in which
 * case the Role is still standing and NOTHING IS APPLIED: reassigning first
 * would move a customer's resources between Roles on the strength of a deletion
 * that may never be approved. The two arms are exclusive, and `reassigned` is
 * absent from the pending arm by construction rather than reported as zero.
 */
export const RoleDeletionPlanResultSchema = z.discriminatedUnion("status", [
  z.strictObject({
    status: z.literal("deleted"),
    /** How many elements were shipped to another Role. */
    reassigned: z.number().int().min(0),
    /** How many were released with the Role. */
    released: z.number().int().min(0)
  }),
  z.strictObject({
    status: z.literal("pending"),
    /** The deletion request awaiting review. No decision was applied. */
    requestId: z.string().uuid()
  })
]);

export type RoleDeletionPlanResult = z.infer<typeof RoleDeletionPlanResultSchema>;
